import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useApi } from '../lib/apiClient';
import { 
  Button, 
  Card, 
  CardContent, 
  Skeleton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  AlertTriangleIcon, 
  PlusCircleIcon, 
  ExternalLinkIcon,
  Trash2 as Trash2Icon,
  Edit as EditIcon
} from '@pagespeedify/ui';
import { ApiResponse, Task } from '../types';
import TaskDetailsModal from '../components/TaskDetailsModal';
import TaskModal from '../components/TaskModal';
import { toast } from 'sonner';
import { DeleteQueueProvider, useDeleteQueue } from '../components/tasks/DeleteQueueContext';
import DeleteQueuePanel from '../components/tasks/DeleteQueuePanel';
import type { TaskType, TaskStatus, TaskPriority } from '../types';

// QueryClient setup
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Wrapper component to provide the QueryClient
export default function TasksPageWrapper() {
  return (
    <QueryClientProvider client={queryClient}>
      <DeleteQueueProvider>
        <TasksPage />
        <DeleteQueuePanel />
      </DeleteQueueProvider>
    </QueryClientProvider>
  );
}

// Helper to normalize priority from different schemas
const getPriorityString = (priority: string | number | undefined | null): 'low' | 'medium' | 'high' => {
    if (typeof priority === 'number') {
      if (priority <= 2) return 'low';
      if (priority === 3) return 'medium';
      if (priority >= 4) return 'high';
    }
    if (typeof priority === 'string') {
        const p = priority.toLowerCase();
        if (p === 'low' || p === 'medium' || p === 'high') {
            return p;
        }
    }
    return 'medium'; // Default for any other case
};

// Task Status Component with badge indicator
const StatusIndicator = ({ status }: { status: string }) => {
  const statusClasses: { [key: string]: string } = {
    open: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    'in_progress': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    resolved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    closed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  };
  const badgeClass = statusClasses[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${badgeClass}`}>
      {status.replace('_', ' ')}
    </span>
  );
};

// Priority Badge Component
const PriorityBadge = ({ priority }: { priority: 'low' | 'medium' | 'high' }) => {
  const priorityClasses: { [key: string]: string } = {
    high: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    medium: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
    low: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  };
  const badgeClass = priorityClasses[priority] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium capitalize ${badgeClass}`}>
      {priority}
    </span>
  );
};

// Main page component
function TasksPage() {
  const apiClient = useApi();
  const queryClient = useQueryClient();
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  // --- Pagination state ---
  const [currentPage, setCurrentPage] = useState(1);
  const TASKS_PER_PAGE = 10;

  const { data: tasksResponse, isLoading, isError, error } = useQuery<ApiResponse<any>, Error>({
    queryKey: ['tasks'],
    queryFn: () => apiClient.get('/tasks'),
    staleTime: 2 * 60 * 1000,
  });

  // Extract backend-provided data
  const backend = tasksResponse?.data?.tasks;
  const tasks: Task[] = Array.isArray(backend?.tasks) ? backend.tasks : [];
  const usage = backend?.usage;
  const canCreate = usage ? usage.canCreate !== false : true;
  const allTags: string[] = Array.isArray(backend?.tags) ? backend.tags : [];
  const allTypes: string[] = Array.isArray(backend?.types) ? backend.types : [];

  // Multi-select state
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const { addTaskToDeleteQueue } = useDeleteQueue();

  const handleViewDetails = (task: Task) => {
    setSelectedTask(task);
    setIsDetailsModalOpen(true);
  };
  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedTask(null);
  };
  const handleOpenEditModal = (task: Task | null) => {
    setSelectedTask(task);
    setIsEditModalOpen(true);
  };
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedTask(null);
  };

  // TODO: Replace with actual user/team info from context or props
  const currentUserId = 'CURRENT_USER_ID'; // Replace with real user id
  const isTeamOwner = true; // Replace with real team owner check
  const canDeleteTask = (task: Task) => {
    if (isTeamOwner) return true;
    return task.user_id === currentUserId;
  };

  // Filter state for dropdowns
  const [filters, setFilters] = useState<{
    status: TaskStatus | 'all';
    priority: TaskPriority | 'all';
    type: TaskType | 'all';
    tag: string | 'all';
  }>({
    status: 'all',
    priority: 'all',
    type: 'all',
    tag: 'all',
  });

  // --- Status options with display labels ---
  const statusOptions: { value: TaskStatus | 'all'; label: string }[] = [
    { value: 'all', label: 'All Statuses' },
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' },
  ];

  // --- Filtering logic ---
  const filteredTasks = tasks.filter(task => {
    if (filters.status !== 'all' && task.status !== filters.status) return false;
    if (filters.priority !== 'all' && getPriorityString(task.priority) !== filters.priority) return false;
    if (filters.type !== 'all' && task.type !== filters.type) return false;
    if (filters.tag !== 'all' && task.tag !== filters.tag) return false;
    return true;
  });
  // --- Pagination logic ---
  const totalPages = Math.max(1, Math.ceil(filteredTasks.length / TASKS_PER_PAGE));
  const pagedTasks = filteredTasks.slice((currentPage - 1) * TASKS_PER_PAGE, currentPage * TASKS_PER_PAGE);
  // Reset to page 1 if filters change and current page is out of range
  React.useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [filteredTasks.length, totalPages]);

  // --- Type options with display labels, only for types present in filteredTasks ---
  const presentTypes = Array.from(new Set(filteredTasks.map(t => t.type).filter((type): type is TaskType => Boolean(type))));
  const typeOptions: { value: TaskType | 'all'; label: string }[] = [
    { value: 'all', label: 'All Types' },
    ...presentTypes.map(type => ({
      value: type,
      label: type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ')
    }))
  ];
  // --- Tag options with display labels ---
  const tagOptions: { value: string; label: string }[] = Array.from(new Set(tasks.map(t => t.tag).filter((tag): tag is string => Boolean(tag))))
    .map(tag => ({ value: tag, label: tag.charAt(0).toUpperCase() + tag.slice(1).replace(/_/g, ' ') }));

  // Render
  if (isLoading) {
    return (
      <div className="space-y-4 p-8">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <AlertTriangleIcon className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">Failed to load tasks</h3>
          <p className="text-muted-foreground">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <header className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Task Management</h1>
        <div className="flex items-center gap-4">
          <Button onClick={() => handleOpenEditModal(null)} disabled={!canCreate}>
            <PlusCircleIcon className="mr-2 h-4 w-4" />
            Create Task
          </Button>
        </div>
      </header>
      <Card>
        <CardContent className="p-4">
          {/* Filter section and Add Selected to Delete Queue button */}
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <Button
                variant="destructive"
                size="sm"
                disabled={selectedTaskIds.length === 0}
                onClick={() => {
                  const selectedTasks = tasks.filter(task => selectedTaskIds.includes(task.id) && canDeleteTask(task));
                  selectedTasks.forEach(task => addTaskToDeleteQueue(task));
                  setSelectedTaskIds([]);
                }}
              >
                Add Selected to Delete Queue ({selectedTaskIds.length})
              </Button>
            </div>
            <div className="flex items-center gap-4 ml-auto">
              {/* Status Filter */}
              <Select value={filters.status} onValueChange={value => setFilters(f => ({ ...f, status: value as TaskStatus | 'all' }))}>
                <SelectTrigger className="w-[140px] bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400">
                  <SelectValue>{statusOptions.find(opt => opt.value === filters.status)?.label ?? filters.status}</SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                  {statusOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 focus:bg-gray-100 dark:focus:bg-gray-800 data-[highlighted]:bg-gray-100 dark:data-[highlighted]:bg-gray-800 data-[highlighted]:text-gray-900 dark:data-[highlighted]:text-gray-100">{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Priority Filter */}
              <Select value={filters.priority} onValueChange={value => setFilters(f => ({ ...f, priority: value as TaskPriority | 'all' }))}>
                <SelectTrigger className="w-[120px] bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400">
                  <SelectValue>{filters.priority === 'all' ? 'All Priorities' : filters.priority}</SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                  <SelectItem value="all" className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 focus:bg-gray-100 dark:focus:bg-gray-800 data-[highlighted]:bg-gray-100 dark:data-[highlighted]:bg-gray-800 data-[highlighted]:text-gray-900 dark:data-[highlighted]:text-gray-100">All Priorities</SelectItem>
                  <SelectItem value="low" className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 focus:bg-gray-100 dark:focus:bg-gray-800 data-[highlighted]:bg-gray-100 dark:data-[highlighted]:bg-gray-800 data-[highlighted]:text-gray-900 dark:data-[highlighted]:text-gray-100">Low</SelectItem>
                  <SelectItem value="medium" className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 focus:bg-gray-100 dark:focus:bg-gray-800 data-[highlighted]:bg-gray-100 dark:data-[highlighted]:bg-gray-800 data-[highlighted]:text-gray-900 dark:data-[highlighted]:text-gray-100">Medium</SelectItem>
                  <SelectItem value="high" className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 focus:bg-gray-100 dark:focus:bg-gray-800 data-[highlighted]:bg-gray-100 dark:data-[highlighted]:bg-gray-800 data-[highlighted]:text-gray-900 dark:data-[highlighted]:text-gray-100">High</SelectItem>
                </SelectContent>
              </Select>
              {/* Type Filter */}
              <Select value={filters.type} onValueChange={value => setFilters(f => ({ ...f, type: value as TaskType | 'all' }))}>
                <SelectTrigger className="w-[140px] bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400">
                  <SelectValue>{typeOptions.find(opt => opt.value === filters.type)?.label ?? filters.type}</SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                  {typeOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 focus:bg-gray-100 dark:focus:bg-gray-800 data-[highlighted]:bg-gray-100 dark:data-[highlighted]:bg-gray-800 data-[highlighted]:text-gray-900 dark:data-[highlighted]:text-gray-100">{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Tag Filter */}
              <Select value={filters.tag} onValueChange={value => setFilters(f => ({ ...f, tag: value }))}>
                <SelectTrigger className="w-[140px] bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400">
                  <SelectValue>{filters.tag === 'all' ? 'All Tags' : (tagOptions.find(opt => opt.value === filters.tag)?.label ?? filters.tag)}</SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                  <SelectItem value="all" className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 focus:bg-gray-100 dark:focus:bg-gray-800 data-[highlighted]:bg-gray-100 dark:data-[highlighted]:bg-gray-800 data-[highlighted]:text-gray-900 dark:data-[highlighted]:text-gray-100">All Tags</SelectItem>
                  {tagOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 focus:bg-gray-100 dark:focus:bg-gray-800 data-[highlighted]:bg-gray-100 dark:data-[highlighted]:bg-gray-800 data-[highlighted]:text-gray-900 dark:data-[highlighted]:text-gray-100">{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Usage info */}
          {usage && (
            <div className="mb-4 text-sm text-muted-foreground">
              {`Tasks used: ${usage.count} / ${usage.limit}`}
            </div>
          )}
          {/* Table */}
          {tasks.length === 0 ? (
            <div className="flex items-center justify-center p-12">
              <div className="text-center">
                <div className="mx-auto h-12 w-12 text-muted-foreground mb-4">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 12l2 2 4-4" />
                    <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3" />
                    <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3" />
                    <path d="M12 3c0 1-1 3-3 3s-3-2-3-3 1-3 3-3 3 2 3 3" />
                    <path d="M12 21c0-1 1-3 3-3s3 2 3 3-1 3-3 3-3-2-3-3" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium mb-2">No tasks found</h3>
                <p className="text-muted-foreground mb-4">You have no tasks at the moment.</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-tour="tasks-list">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="p-4">
                      <input
                        type="checkbox"
                        checked={pagedTasks.filter(canDeleteTask).length > 0 && pagedTasks.filter(canDeleteTask).every(task => selectedTaskIds.includes(task.id))}
                        onChange={e => {
                          if (e.target.checked) {
                            // Select all tasks on current page that can be deleted
                            const currentPageDeletableTasks = pagedTasks.filter(canDeleteTask).map(t => t.id);
                            setSelectedTaskIds(prev => [...new Set([...prev, ...currentPageDeletableTasks])]);
                          } else {
                            // Deselect all tasks on current page
                            const currentPageTaskIds = pagedTasks.map(t => t.id);
                            setSelectedTaskIds(prev => prev.filter(id => !currentPageTaskIds.includes(id)));
                          }
                        }}
                        aria-label="Select all tasks on current page"
                      />
                    </th>
                    <th className="text-left p-4 font-medium">TASK</th>
                    <th className="text-left p-4 font-medium">STATUS</th>
                    <th className="text-left p-4 font-medium">PRIORITY</th>
                    <th className="text-left p-4 font-medium">TYPE</th>
                    <th className="text-left p-4 font-medium">GITHUB ISSUE</th>
                    <th className="text-left p-4 font-medium">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedTasks.map((task) => (
                    <tr
                      key={task.id}
                      className="hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={e => {
                        if (e.target instanceof HTMLElement && e.target.tagName.toLowerCase() === 'input') return;
                        handleViewDetails(task);
                      }}
                    >
                      <td className="p-4">
                        <input
                          type="checkbox"
                          checked={selectedTaskIds.includes(task.id)}
                          disabled={!canDeleteTask(task)}
                          onClick={e => e.stopPropagation()}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedTaskIds(ids => [...ids, task.id]);
                            } else {
                              setSelectedTaskIds(ids => ids.filter(id => id !== task.id));
                            }
                          }}
                          aria-label={`Select task ${task.title}`}
                        />
                      </td>
                      <td className="p-4">
                        <div className="font-medium">{task.title || 'No Title'}</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {task.context?.pageUrl || task.context?.websiteUrl || ''}
                        </div>
                        {task.created_by?.label && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Created by: {task.created_by.label}
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <StatusIndicator status={task.status} />
                      </td>
                      <td className="p-4">
                        <PriorityBadge priority={getPriorityString(task.priority)} />
                      </td>
                      <td className="p-4 capitalize">
                        {typeOptions.find(opt => opt.value === task.type)?.label ?? task.type}
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {task.github_issue_url ? (
                          <a 
                            href={task.github_issue_url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="flex items-center text-blue-600 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLinkIcon className="h-4 w-4 mr-1" />
                            <span>View Issue</span>
                          </a>
                        ) : (
                          'Not linked'
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEditModal(task);
                            }}
                            data-tour="edit-task-button"
                          >
                            <EditIcon className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm" 
                            onClick={e => {
                              e.stopPropagation();
                              if (canDeleteTask(task)) addTaskToDeleteQueue(task);
                            }} 
                            disabled={!canDeleteTask(task)}
                          >
                            <Trash2Icon className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Pagination controls */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                    Prev
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <Button
                      key={page}
                      size="sm"
                      variant={page === currentPage ? 'default' : 'outline'}
                      onClick={() => setCurrentPage(page)}
                      className={page === currentPage ? 'font-bold' : ''}
                    >
                      {page}
                    </Button>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      {isDetailsModalOpen && selectedTask && (
        <TaskDetailsModal 
          task={selectedTask} 
          isOpen={isDetailsModalOpen} 
          onClose={handleCloseDetailsModal} 
        />
      )}
      {isEditModalOpen && (
        <TaskModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          task={selectedTask}
          allTags={allTags || []}
          onSuccess={(result?: { success?: boolean }) => {
            if (result && result.success) {
              queryClient.refetchQueries({ queryKey: ['tasks'], exact: true });
            }
          }}
        />
      )}
    </div>
  );
}
